import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import type { PerformanceSnapshotApi } from "@/types/api/performance-snapshot";

/**
 * Demo data that matches what the existing dashboard renders via
 * useHomepageMetrics + useFilteredMetrics in demo mode.
 */
const demoData: PerformanceSnapshotApi = {
  customerCount: 350,
  dso: 42,
  dsoTarget: 40,
  promiseSuccessPercentage: 53,
  promisesKept: 21,
  promisesTotal: 40,
  avgPaymentDays: 18,
  collectionRate: 53,
  collectionRateTarget: 85,
  collectedAmount: 2300000,
  totalDueAmount: 4300000,
};

/**
 * Hook to fetch Performance Snapshot data for the Credit Control Dashboard.
 *
 * In **live** mode it calls:
 *   GET /api/v1/analysis/performance-snapshot?userId={userId}
 *
 * In **demo** mode it returns the hardcoded snapshot data.
 *
 * @param selectedUserId  Pass a user ID string when a specific user is selected,
 *                        or `undefined` for "Everyone".
 */
export function usePerformanceSnapshot(selectedUserId?: string) {
  const { apiGet } = useApi();

  return useModeAwareQuery<PerformanceSnapshotApi, Error>(
    ["performance-snapshot", selectedUserId ?? "everyone"],
    async ({ signal }) => {
      const params: Record<string, string> = {};
      if (selectedUserId) {
        params.userId = selectedUserId;
      }

      return apiGet<PerformanceSnapshotApi>(
        "/api/v1/analysis/performance-snapshot",
        params,
        signal
      );
    },
    demoData,
    { staleTime: 0, refetchOnMount: "always", refetchOnWindowFocus: false }
  );
}
